import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { useSessionUser } from '../../SessionContext';

const ChatNotification = ({ onOpenSession }) => {
  const { unreadCounts, activeSession, getSessionSummary } = useChat();
  const { user } = useSessionUser();
  const [showNotification, setShowNotification] = useState(false);
  const lastObservedUnreadRef = useRef({});

  const unreadSessions = useMemo(
    () =>
      Object.entries(unreadCounts)
        .filter(([_, count]) => Number(count) > 0)
        .map(([sessionId, count]) => {
          const summary = getSessionSummary(sessionId);
          return {
            sessionId,
            count: Number(count) || 0,
            peerId: String(summary?.peerId || '').trim()
          };
        })
        .filter((item) => item.sessionId)
        .sort((left, right) => right.count - left.count),
    [getSessionSummary, unreadCounts]
  );

  const totalUnread = useMemo(
    () => unreadSessions.reduce((sum, item) => sum + item.count, 0),
    [unreadSessions]
  );

  useEffect(() => {
    const nextUnreadMap = unreadSessions.reduce((acc, item) => {
      acc[item.sessionId] = item.count;
      return acc;
    }, {});

    const hasNewUnread = unreadSessions.some(
      (item) => item.count > Number(lastObservedUnreadRef.current[item.sessionId] || 0)
    );

    if (hasNewUnread && !activeSession) {
      setShowNotification(true);
      lastObservedUnreadRef.current = nextUnreadMap;

      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 7000);

      return () => clearTimeout(timer);
    }

    if (!unreadSessions.length) {
      setShowNotification(false);
    }

    lastObservedUnreadRef.current = nextUnreadMap;
  }, [activeSession, unreadSessions]);

  const handleOpenSession = (sessionId) => {
    if (typeof onOpenSession === 'function') {
      onOpenSession(sessionId);
    }
    setShowNotification(false);
  };

  if (!showNotification || !user || !unreadSessions.length) return null;

  return (
    <div className="toast-container position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1100 }}>
      <div className="toast show shadow-sm border-0" role="alert" aria-live="assertive" aria-atomic="true">
        <div className="toast-header bg-primary text-white">
          <strong className="me-auto">New Messages</strong>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={() => setShowNotification(false)}
          ></button>
        </div>
        <div className="toast-body">
          <div className="mb-3">
            {totalUnread === 1 ? 'You have 1 new message' : `You have ${totalUnread} new messages`}
          </div>

          <div className="d-flex flex-column gap-2">
            {unreadSessions.slice(0, 3).map((item) => {
              const label = item.peerId ? `Agent ${item.peerId}` : 'Unread chat';
              const countLabel = item.count === 1 ? '1 message' : `${item.count} messages`;

              return (
                <div key={item.sessionId} className="d-flex align-items-center justify-content-between gap-2">
                  <div className="small text-muted">
                    {label} - {countLabel}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleOpenSession(item.sessionId)}
                  >
                    Open Chat
                  </button>
                </div>
              );
            })}
          </div>

          {unreadSessions.length > 3 && (
            <div className="small text-muted mt-2">
              +{unreadSessions.length - 3} more chats
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatNotification;
